import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteNotificationById,
  fetchNotificationsByUserId,
  markNotificationAsRead,
  notificationsListSelector,
  notificationsLoadingSelector,
  unreadNotificationsCountSelector
} from '../../redux/features/notification/notificationsSlice';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/styles';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';

const useStyles = makeStyles((theme) => ({
  iconButton: {},
  badge: {},
  notificationItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  unreadNotification: {
    backgroundColor: 'rgba(63, 81, 181, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(63, 81, 181, 0.12)'
    }
  },
  notificationContent: {
    flex: 1,
    marginRight: theme.spacing(2),
    minWidth: 0
  },
  notificationTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    fontSize: '0.9rem'
  },
  notificationBody: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  menuTitle: {
    fontWeight: 600,
    fontSize: '1rem'
  },
  connectionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3)
  },
  menuPaper: {
    maxHeight: '80vh', // Use viewport height to adapt to screen size
    minWidth: 360,
    maxWidth: 420,
    marginTop: theme.spacing(1),
    overflow: 'hidden' // Prevent overflow on the paper itself
  },
  menuHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  notificationsList: {
    maxHeight: 'calc(80vh - 70px)', // Account for header height
    overflowY: 'auto',
    overflowX: 'hidden'
  }
}));

const NotificationCenter = ({ userId, enableWebSocket = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Redux state
  const notifications = useSelector(notificationsListSelector);
  const unreadCount = useSelector(unreadNotificationsCountSelector);
  const isLoading = useSelector(notificationsLoadingSelector);

  // Log when component mounts with userId
  useEffect(() => {
    if (userId) {
      console.log('🔔 [NotificationCenter] Mounted with userId:', userId);
    } else {
      console.log('⏳ [NotificationCenter] Waiting for userId...');
    }
  }, [userId]);

  // PART 1: Fetch ALL existing notifications via REST API
  useEffect(() => {
    if (userId) {
      dispatch(fetchNotificationsByUserId({ userId }));
    }
  }, [userId, dispatch]);

  // PART 2: Open WebSocket connection for LIVE notifications
  const { isConnected } = useNotificationWebSocket(
    userId,
    enableWebSocket && !!userId
  );

  // Log WebSocket connection status changes
  useEffect(() => {
    if (userId) {
      console.log(
        isConnected
          ? '✅ [NotificationCenter] WebSocket CONNECTED - Ready for live notifications'
          : '⚠️ [NotificationCenter] WebSocket DISCONNECTED'
      );
    }
  }, [isConnected, userId]);

  // Don't render if userId is not available yet
  if (!userId) {
    return null;
  }

  const handleMenuOpen = async (event) => {
    setAnchorEl(event.currentTarget);

    // Mark all unread notifications as read when opening the menu
    const unreadNotifications = notifications.filter((n) => !n.read);

    if (unreadNotifications.length > 0) {
      console.log(
        `📖 [NotificationCenter] Marking ${unreadNotifications.length} notifications as read`
      );

      // Mark all unread notifications as read
      for (const notification of unreadNotifications) {
        try {
          dispatch(
            markNotificationAsRead({
              notificationId: notification.id,
              notification
            })
          );
        } catch (error) {
          console.error(
            `❌ Failed to mark notification ${notification.id} as read:`,
            error
          );
        }
      }
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        dispatch(
          markNotificationAsRead({
            notificationId: notification.id,
            notification
          })
        );
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const handleDeleteNotification = async (notificationId, event) => {
    event.stopPropagation();

    try {
      dispatch(deleteNotificationById({ notificationId }));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const renderNotificationItem = (notification) => {
    const isUnread = !notification.read;

    return (
      <MenuItem
        key={notification.id}
        className={`${classes.notificationItem} ${
          isUnread ? classes.unreadNotification : ''
        }`}
        onClick={() => handleNotificationClick(notification)}
        disableRipple>
        <Box className={classes.notificationContent}>
          <Typography className={classes.notificationTitle}>
            {isUnread && '● '}
            {notification.topic}
          </Typography>
          <Typography className={classes.notificationBody}>
            {notification.body}
          </Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.deleteButton}
          onClick={(e) => handleDeleteNotification(notification.id, e)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </MenuItem>
    );
  };

  return (
    <>
      <IconButton
        className={classes.iconButton}
        onClick={handleMenuOpen}
        aria-label="notifications">
        <Badge
          badgeContent={unreadCount}
          color="secondary"
          overlap="rectangular"
          classes={{ badge: classes.badge }}>
          <NotificationsOutlinedIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          className: classes.menuPaper
        }}>
        {/* Fixed Header */}
        <Box className={classes.menuHeader}>
          <Typography variant="h6" className={classes.menuTitle}>
            Notifications
          </Typography>
          {enableWebSocket && (
            <Box className={classes.connectionStatus}>
              <Typography
                variant="caption"
                style={{
                  color: isConnected ? '#4caf50' : '#9e9e9e',
                  fontWeight: 500
                }}>
                {isConnected ? '● Live' : '○ Offline'}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider />

        {/* Scrollable Content */}
        <Box className={classes.notificationsList}>
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={30} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box className={classes.emptyState}>
              <NotificationsIcon
                style={{ fontSize: 48, color: '#ccc', marginBottom: 8 }}
              />
              <Typography variant="body2" color="textSecondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            notifications.map(renderNotificationItem)
          )}
        </Box>
      </Menu>
    </>
  );
};

NotificationCenter.propTypes = {
  userId: PropTypes.string,
  enableWebSocket: PropTypes.bool
};

export default NotificationCenter;
